"use client";

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { isValidVariant } from "../lib/config/ab-tests";
import { getABTestVariant, setABTestVariant } from "../lib/utils/ab-test-cookies";
import { trackABTestAssigned, trackABTestView } from "../lib/utils/analytics";

interface ABTestContextType {
	variant: string | null;
	setVariant: (variant: string | null) => void;
}

const ABTestContext = createContext<ABTestContextType>({
	variant: null,
	setVariant: () => {},
});

export function useABTestContext() {
	return useContext(ABTestContext);
}

export default function ABTestProvider({ children }: { children: React.ReactNode }) {
	const [searchParams] = useSearchParams();
	const [variant, setVariantState] = useState<string | null>(null);

	const setVariant = useCallback((newVariant: string | null) => {
		setVariantState(newVariant);
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") return;

		// Get variant from URL parameter
		const urlVariant = searchParams.get("v");

		if (urlVariant) {
			// Validate variant from URL
			if (isValidVariant(urlVariant)) {
				// Valid variant: set it and store in cookie
				setVariantState(urlVariant);
				setABTestVariant(urlVariant);
				trackABTestAssigned(urlVariant);
				trackABTestView(urlVariant);
			} else {
				// Invalid variant: check if we have a valid cookie
				const cookieVariant = getABTestVariant();
				if (cookieVariant && isValidVariant(cookieVariant)) {
					// Use valid cookie variant, don't overwrite cookie
					setVariantState(cookieVariant);
				} else {
					// No valid variant, show default
					setVariantState(null);
				}
			}
		} else {
			// No URL parameter: check cookie
			const cookieVariant = getABTestVariant();
			if (cookieVariant && isValidVariant(cookieVariant)) {
				// Valid cookie variant
				setVariantState(cookieVariant);
				trackABTestView(cookieVariant);
			} else {
				// No valid variant, show default
				setVariantState(null);
			}
		}

	}, [searchParams]);

	return (
		<ABTestContext.Provider value={{ variant, setVariant }}>
			{children}
		</ABTestContext.Provider>
	);
}

